import { createRoute, lazyRouteComponent, Outlet, redirect, type AnyRoute } from "@tanstack/react-router";
import { ApiError } from "@/lib/api";
import { loadStaffMe } from "./api";
import { StaffJoinPage, StaffSignInPage } from "./auth/SignIn";
import { StaffPreviewEntry } from "./preview/PreviewEntry";
import { StaffShell } from "./StaffShell";

/*
 * The staff app: the same build, its own route tree under /staff and its own
 * shell. Owners and managers who land here go back to the desk (except the
 * Plan's preview); signed out goes to the staff sign-in, not the desk's.
 */

const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
const page = <T extends Record<string, unknown>, K extends keyof T & string>(loader: () => Promise<T>, name: K) => lazyRouteComponent(loader, name);

export function staffRoutes<R extends AnyRoute>(rootRoute: R) {
  const base = createRoute({ getParentRoute: () => rootRoute, path: "/staff", component: Outlet });

  const signIn = createRoute({
    getParentRoute: () => base,
    path: "/sign-in",
    validateSearch: (s: Record<string, unknown>): { next?: string } => ({ next: str(s.next) }),
    component: StaffSignInPage,
  });
  const join = createRoute({ getParentRoute: () => base, path: "/join/$token", component: StaffJoinPage });
  const preview = createRoute({
    getParentRoute: () => base,
    path: "/preview",
    validateSearch: (s: Record<string, unknown>): { code?: string } => ({ code: str(s.code) }),
    component: StaffPreviewEntry,
  });

  const app = createRoute({
    getParentRoute: () => base,
    id: "app",
    beforeLoad: async ({ location }) => {
      try {
        return { staffMe: await loadStaffMe() };
      } catch (e) {
        if (e instanceof ApiError) {
          // A desk session (owner, manager) on the staff app: back to the desk.
          if (e.code === "staff_app_only") throw redirect({ to: "/" });
          if (e.status === 401) throw redirect({ to: "/staff/sign-in", search: { next: location.href === "/staff" ? undefined : location.href } });
        }
        throw e;
      }
    },
    component: StaffShell,
  });

  const home = createRoute({ getParentRoute: () => app, path: "/", component: page(() => import("./home/Home"), "StaffHome") });
  const checks = createRoute({ getParentRoute: () => app, path: "/checks", component: page(() => import("./checks/Checks"), "StaffChecks") });
  const run = createRoute({ getParentRoute: () => app, path: "/checks/$taskId", component: page(() => import("./checks/RunCheck"), "StaffRunCheck") });
  const repairs = createRoute({
    getParentRoute: () => app,
    path: "/repairs",
    validateSearch: (s: Record<string, unknown>): { tab?: "mine" | "reported" } => ({ tab: s.tab === "mine" || s.tab === "reported" ? s.tab : undefined }),
    component: page(() => import("./repairs/Repairs"), "StaffRepairs"),
  });
  const repairNew = createRoute({ getParentRoute: () => app, path: "/repairs/new", component: page(() => import("./repairs/Repairs"), "StaffReportRepair") });
  const repair = createRoute({ getParentRoute: () => app, path: "/repairs/$issueId", component: page(() => import("./repairs/Repairs"), "StaffRepair") });
  const laundry = createRoute({ getParentRoute: () => app, path: "/laundry", component: page(() => import("./laundry/Laundry"), "StaffLaundry") });
  const send = createRoute({ getParentRoute: () => app, path: "/laundry/send", component: page(() => import("./laundry/Laundry"), "StaffSendLaundry") });
  const receive = createRoute({ getParentRoute: () => app, path: "/laundry/$batchId/receive", component: page(() => import("./laundry/Laundry"), "StaffReceiveLaundry") });
  const keys = createRoute({ getParentRoute: () => app, path: "/keys", component: page(() => import("./keys/Keys"), "StaffKeys") });
  const code = createRoute({ getParentRoute: () => app, path: "/keys/$roomId", component: page(() => import("./keys/Keys"), "StaffChangeCode") });
  const lost = createRoute({ getParentRoute: () => app, path: "/lost", component: page(() => import("./lost/Lost"), "StaffLost") });
  const lostNew = createRoute({ getParentRoute: () => app, path: "/lost/new", component: page(() => import("./lost/Lost"), "StaffLogFound") });
  // Anything else under /staff: Home.
  const rest = createRoute({
    getParentRoute: () => app,
    path: "$",
    beforeLoad: () => {
      throw redirect({ to: "/staff" });
    },
  });

  return base.addChildren([signIn, join, preview, app.addChildren([home, checks, run, repairs, repairNew, repair, laundry, send, receive, keys, code, lost, lostNew, rest])]);
}
